import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"
import { confirmAction } from "@/lib/dialogs"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef(({ className, children, guardUnsaved = true, onEscapeKeyDown, onInteractOutside, onInputCapture, onSubmitCapture, ...props }, ref) => {
  const inner = React.useRef(null)
  const closer = React.useRef(null)
  const dirty = React.useRef(false)
  const setRefs = (node) => {
    inner.current = node
    if (typeof ref === "function") ref(node)
    else if (ref) ref.current = node
  }
  // A form that has been edited asks before Escape, a click outside or the X throws the edits away. A Cancel button inside the
  // form is a deliberate choice and closes straight away; so does a dialog with no form (a viewer, a picker).
  const askBeforeClosing = (event) => {
    if (!guardUnsaved || !dirty.current || !inner.current?.querySelector("form")) return
    event?.preventDefault()
    confirmAction({
      kind: "warning", title: "Discard your changes?", message: "You have unsaved changes in this form. If you close it now they will be lost.",
      confirmLabel: "Discard changes", cancelLabel: "Keep editing",
    }).then((ok) => { if (ok) { dirty.current = false; closer.current?.click() } })
  }
  return (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={setRefs}
      onEscapeKeyDown={(e) => { onEscapeKeyDown?.(e); if (!e.defaultPrevented) askBeforeClosing(e) }}
      onInteractOutside={(e) => { onInteractOutside?.(e); if (!e.defaultPrevented) askBeforeClosing(e) }}
      onInputCapture={(e) => { if (e.target?.closest?.("form")) dirty.current = true; onInputCapture?.(e) }}
      onSubmitCapture={(e) => { dirty.current = false; onSubmitCapture?.(e) }}
      className={cn(
        "fixed left-[50%] top-[50%] z-50 grid max-h-[90vh] w-[calc(100%-1rem)] max-w-lg overflow-y-auto sm:w-full [&>*]:min-w-0 translate-x-[-50%] translate-y-[-50%] gap-4 border border-border bg-background p-4 shadow-xl duration-200 sm:p-6 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] rounded-xl",
        className
      )}
      {...props}>
      {children}
      <DialogPrimitive.Close
        aria-label="Close"
        onClick={askBeforeClosing}
        className="absolute right-4 top-4 z-10 rounded-md p-1 opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
      <DialogPrimitive.Close ref={closer} tabIndex={-1} aria-hidden="true" className="sr-only" />
    </DialogPrimitive.Content>
  </DialogPortal>
  )
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("sticky -bottom-6 -mb-6 flex flex-col-reverse border-t border-border/60 bg-background pb-6 pt-3 sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
